/**
 * Compact banner shown when the active match is synthetic/simulated.
 * Public copy never uses the word "DEMO" — VerseSport is not a demo app.
 * The simulated fixture is labelled neutrally instead.
 */
import { useLanguage } from "@/hooks/useLanguage";

export interface DemoModeBannerProps {
  variant?: "admin" | "preview";
  onExit?: () => void;
}

export function DemoModeBanner({ variant = "admin", onExit }: DemoModeBannerProps = {}) {
  const isPreview = variant === "preview";
  const { lang } = useLanguage();
  const main =
    lang === "EN"
      ? "No live matches right now"
      : lang === "PT"
      ? "Não há partidas ao vivo agora"
      : "No hay partidos en vivo ahora";
  const sub =
    lang === "EN"
      ? "This view is a simulated match"
      : lang === "PT"
      ? "Esta vista é uma simulação da partida"
      : "Esta vista es una simulación del partido";
  const exitLabel = lang === "EN" ? "Close" : lang === "PT" ? "Fechar" : "Salir";
  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full border-y border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-amber-100"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 text-center min-h-[36px]">
        <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-amber-400" aria-hidden />
        <span className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-200 leading-tight">
          {main}
        </span>
        <span className="hidden text-amber-300/60 sm:inline">·</span>
        <span className="truncate text-[11px] font-medium text-amber-100/80 leading-tight">
          {sub}
        </span>
        {isPreview && onExit && (
          <button
            type="button"
            onClick={onExit}
            className="ml-1 shrink-0 rounded border border-amber-400/60 bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-50 hover:bg-amber-500/35"
          >
            {exitLabel}
          </button>
        )}
      </div>
    </div>
  );
}